import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, type PointerEvent } from "react";
import { CaptureBucket, CaptureDateStats } from "../capture-date-stats";
import { Bounds } from "./capture-calendar-types";

/** How long a drag must rest on a page edge or paging arrow before the page turns */
const PAGE_TURN_DELAY_MS = 800;

/**
 * @returns A pluralized capture count, for tooltips
 * @param count How many captures the cell holds
 */
export function captureLabel(count: number): string {
  return `${count} ${count === 1 ? "capture" : "captures"}`;
}

/**
 * @returns The span the user may navigate and select within: from the month of the first
 * capture up to today, so no screen is empty of captures or entirely in the future
 * @param stats The project capture histogram
 */
export function useSelectableBounds(stats: CaptureDateStats): Bounds {
  return useMemo(() => {
    const now = DateTime.now();
    const first = stats.days.length
      ? DateTime.fromJSDate(stats.days[0].start)
      : now;
    return { min: first.startOf("month"), max: now.endOf("day") };
  }, [stats]);
}

/**
 * @returns A lookup from bucket start (in millis) to capture count
 * @param buckets The buckets to index
 */
export function useBucketCounts(buckets: CaptureBucket[]): Map<number, number> {
  return useMemo(
    () => new Map(buckets.map(({ start, count }) => [start.getTime(), count])),
    [buckets],
  );
}

/**
 * Touch captures the pointer to the pressed cell, hiding the cells a finger drags over;
 * releasing it lets them report hovers as they do for a mouse.
 *
 * @param event The pointerdown on a cell
 */
export function releaseTouchCapture({
  pointerType,
  pointerId,
  target,
}: PointerEvent): void {
  if (pointerType === "touch" && target instanceof Element) {
    target.releasePointerCapture(pointerId);
  }
}

/** Turns the page once a drag has rested on a page edge for a moment */
export type PageTurnOnRest = {
  /** The pointer entered a page edge; `turn` runs if it is still there after the delay */
  rest(turn: () => void): void;
  /** The pointer left the page edge */
  leave(): void;
};

/**
 * @returns Handlers that turn the page when a drag rests on a page edge
 * @param isDragging True while a range is dragged; resting does nothing otherwise
 */
export function usePageTurnOnRest(isDragging?: boolean): PageTurnOnRest {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Cancelled when the drag ends, so a release on a page edge does not turn the page later
  useEffect(() => () => clearTimeout(timer.current), [isDragging]);
  return useMemo(
    () => ({
      rest(turn) {
        clearTimeout(timer.current);
        if (isDragging) timer.current = setTimeout(turn, PAGE_TURN_DELAY_MS);
      },
      leave() {
        clearTimeout(timer.current);
      },
    }),
    [isDragging],
  );
}
